import { forwardRef } from 'react';
import { Controller, ControllerProps, FieldValues, Path } from 'react-hook-form';
import NumberFormat, { InputAttributes } from 'react-number-format';

import { TextField, TextFieldProps } from '@mui/material';

import styled from 'styled-components';

type FormNumberFieldProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues>> = {
  controllerProps: Omit<ControllerProps<TFieldValues, TName>, 'render'>;
} & TextFieldProps;

export const transformNumberToString = (input: number | undefined, defaultValue = ''): string => {
  if (input == null) {
    return defaultValue;
  }
  return isNaN(input) ? defaultValue : input.toString();
};

export const transformStringToNumber = (value: string, defaultValue: string | undefined = undefined) => {
  const parsed = parseFloat(value);
  return isNaN(parsed) ? defaultValue : parsed;
};

const StyledFormNumberField = styled(TextField)`
  input {
    // Disables the arrow inputs
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    // Disables the arrow inputs
    & {
      -moz-appearance: textfield;
    }
  }
`;

export const FormNumberField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>
>({
  controllerProps,
  ...textFieldProps
}: FormNumberFieldProps<TFieldValues, TName>) => {
  return (
    <Controller
      {...controllerProps}
      render={({ field: { onChange, value, ref } }) => (
        <StyledFormNumberField
          {...textFieldProps}
          inputRef={ref}
          onChange={(event) => onChange(transformStringToNumber(event.target.value))}
          value={value ?? ''}
          type="number"
        />
      )}
    />
  );
};

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

export const NumberFormatComponent = forwardRef<NumberFormat<InputAttributes>, CustomProps>((props, ref) => {
  const { onChange, ...other } = props;

  return (
    <NumberFormat
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      {...other}
    />
  );
});
